import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000BasicDialog from "../../../../u2000Core/components/dialog/U2000BasicDialog";
import { useU2000AuthContext } from "../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import { u2000TodayOrgYmd } from "../../../../u2000Core/tools/U2000DateTimeHelpers";
import { NS, U3101ROLES } from "../../../U3101";
import {
  type U3101SalaryHistory,
  type U3101SalaryHistoryApplyMode,
  type U3101SalaryHistoryEntry,
  type U3101SalaryHistoryStatus,
} from "../../../U3101Service";
import { useU3101SalaryHistoryHandlers } from "../../../U3101UseSalaryHistoryHandlers";
import U3101SalaryHistoryEntryDlg, {
  type U3101SalaryHistoryDlgRow,
} from "./U3101SalaryHistoryEntryDlg";

export interface U3101SalaryHistoryTabProps {
  employeeId: string;
  /** Après apply now : resync fiche (hourRate / type / base). */
  onSalaryApplied?: () => void;
  /** Après mutation : resync verrou rémunération côté formulaire. */
  onHistoryChanged?: (history: U3101SalaryHistory) => void;
}

type DialogMode = { kind: "create" } | { kind: "edit"; entry: U3101SalaryHistoryEntry };

function statusLabelKey(status: U3101SalaryHistoryStatus): string {
  if (status === "current") return "valSalaryHistoryStatusCurrent";
  if (status === "planned") return "valSalaryHistoryStatusPlanned";
  return "valSalaryHistoryStatusHistorical";
}

function statusBadgeClass(status: U3101SalaryHistoryStatus): string {
  if (status === "current") return "bg-emerald-100 text-emerald-800";
  if (status === "planned") return "bg-sky-100 text-sky-800";
  return "bg-slate-200 text-slate-800";
}

function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const U3101SalaryHistoryTab: React.FC<U3101SalaryHistoryTabProps> = ({
  employeeId,
  onSalaryApplied,
  onHistoryChanged,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const canEdit = hasRole(U3101ROLES.EDIT);
  const { fetch, create, update, remove } = useU3101SalaryHistoryHandlers();

  const [history, setHistory] = useState<U3101SalaryHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [cancelTarget, setCancelTarget] = useState<U3101SalaryHistoryEntry | null>(null);

  const todayYmd = useMemo(() => u2000TodayOrgYmd(), []);

  const loadHistory = useCallback(async () => {
    const id = employeeId?.trim();
    if (!id) return;
    setLoading(true);
    setLoadFailed(false);
    const result = await fetch(id);
    setHistory(result);
    setLoadFailed(result == null);
    setLoading(false);
    if (result) {
      onHistoryChanged?.(result);
    }
  }, [employeeId, fetch, onHistoryChanged]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const entries = useMemo(() => history?.entries ?? [], [history]);
  const hasApprovedTimesheet = history?.hasApprovedTimesheet === true;

  const openCreate = () => setDialogMode({ kind: "create" });
  const openEdit = (entry: U3101SalaryHistoryEntry) => setDialogMode({ kind: "edit", entry });
  const closeDialog = () => {
    if (!actionBusy) setDialogMode(null);
  };

  const applyHistoryResult = (result: U3101SalaryHistory, applyMode?: U3101SalaryHistoryApplyMode) => {
    setHistory(result);
    onHistoryChanged?.(result);
    if (applyMode === "now") {
      onSalaryApplied?.();
    }
  };

  const handleSaveDialog = async (row: U3101SalaryHistoryDlgRow) => {
    const id = employeeId?.trim();
    if (!id || !dialogMode) return;

    const hourRate = row.hourRate;
    if (hourRate === null || hourRate === undefined) return;

    if (dialogMode.kind === "create") {
      const applyMode = row.applyMode;
      let effectiveDate = row.effectiveDate;
      if (applyMode === "now") {
        effectiveDate = u2000TodayOrgYmd();
      } else if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate) || effectiveDate <= todayYmd) {
        return;
      }
      setActionBusy(true);
      const result = await create(id, {
        applyMode,
        effectiveDate,
        remunerationType: row.remunerationType,
        hourRate,
        hourlyBase: row.hourlyBase,
        comment: row.comment.trim() === "" ? null : row.comment.trim(),
      });
      setActionBusy(false);
      if (!result) return;
      applyHistoryResult(result, applyMode);
      const successKey =
        applyMode === "now"
          ? hasApprovedTimesheet
            ? "msgSalaryHistoryCreateNowWithFtSuccess"
            : "msgSalaryHistoryCreateNowInPlaceSuccess"
          : "msgSalaryHistoryCreateLaterSuccess";
      toast.success(t(successKey, { ns: NS }));
      setDialogMode(null);
      return;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(row.effectiveDate) || row.effectiveDate <= todayYmd) {
      return;
    }
    setActionBusy(true);
    const result = await update(id, dialogMode.entry.id, {
      effectiveDate: row.effectiveDate,
      remunerationType: row.remunerationType,
      hourRate,
      hourlyBase: row.hourlyBase,
      comment: row.comment.trim() === "" ? null : row.comment.trim(),
    });
    setActionBusy(false);
    if (!result) return;
    applyHistoryResult(result);
    toast.success(t("msgSalaryHistoryUpdateSuccess", { ns: NS }));
    setDialogMode(null);
  };

  const handleConfirmCancel = async () => {
    const id = employeeId?.trim();
    if (!id || !cancelTarget) return;
    setActionBusy(true);
    const result = await remove(id, cancelTarget.id);
    setActionBusy(false);
    if (!result) return;
    applyHistoryResult(result);
    toast.success(t("msgSalaryHistoryCancelSuccess", { ns: NS }));
    setCancelTarget(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">{t("msgSalaryHistoryLoading", { ns: NS })}</p>
      </div>
    );
  }

  if (loadFailed || !history) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-gray-600">{t("msgSalaryHistoryLoadError", { ns: NS })}</p>
        <U2000ActionBtn label={t("btnRefreshSalaryHistory", { ns: NS })} handleClick={() => void loadHistory()} />
      </div>
    );
  }

  return (
    <div className="space-y-3 py-2">
      {!hasApprovedTimesheet ? (
        <p className="rounded-lg border border-sky-200 bg-sky-50/80 px-3 py-2 text-sm text-sky-900">
          {t("msgSalaryHistoryNoApprovedFtHint", { ns: NS })}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {canEdit ? (
          <U2000ActionBtn
            label={t("btnAddSalaryHistory", { ns: NS })}
            handleClick={openCreate}
            disabled={actionBusy}
          />
        ) : null}
        <U2000ActionBtn
          label={t("btnRefreshSalaryHistory", { ns: NS })}
          handleClick={() => void loadHistory()}
          variant="secondary"
          disabled={actionBusy}
        />
      </div>

      {entries.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-600">{t("msgSalaryHistoryEmpty", { ns: NS })}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">
                  {t("colSalaryHistoryEffectiveDate", { ns: NS })}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">
                  {t("colSalaryHistoryRemunerationType", { ns: NS })}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">
                  {t("colSalaryHistoryHourRate", { ns: NS })}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">
                  {t("colSalaryHistoryHourlyBase", { ns: NS })}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">
                  {t("colSalaryHistoryStatus", { ns: NS })}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">
                  {t("colSalaryHistoryComment", { ns: NS })}
                </th>
                {canEdit ? (
                  <th className="px-3 py-2 text-right font-medium text-gray-700">
                    {t("colSalaryHistoryActions", { ns: NS })}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-900">{entry.effectiveDate}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-900">
                    {entry.remunerationType === "Salary"
                      ? t("valRemunerationTypeSalary", { ns: NS })
                      : t("valRemunerationTypeHourly", { ns: NS })}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-gray-900">
                    {formatMoney(entry.hourRate)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-gray-900">
                    {entry.hourlyBase == null ? "—" : formatMoney(entry.hourlyBase)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(entry.status)}`}
                    >
                      {t(statusLabelKey(entry.status), { ns: NS })}
                    </span>
                  </td>
                  <td className="max-w-xs truncate px-3 py-2 text-gray-700" title={entry.comment ?? undefined}>
                    {entry.comment?.trim() ? entry.comment : "—"}
                  </td>
                  {canEdit ? (
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {entry.status === "planned" ? (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50"
                            disabled={actionBusy}
                            onClick={() => openEdit(entry)}
                          >
                            {t("btnEditSalaryHistory", { ns: NS })}
                          </button>
                          <button
                            type="button"
                            className="text-sm font-medium text-red-600 hover:underline disabled:opacity-50"
                            disabled={actionBusy}
                            onClick={() => setCancelTarget(entry)}
                          >
                            {t("btnCancelSalaryHistory", { ns: NS })}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <U3101SalaryHistoryEntryDlg
        isOpen={dialogMode !== null}
        dialogKind={dialogMode?.kind ?? "create"}
        history={history}
        editEntry={dialogMode?.kind === "edit" ? dialogMode.entry : undefined}
        busy={actionBusy}
        onClose={closeDialog}
        onSave={handleSaveDialog}
      />

      {cancelTarget ? (
        <U2000BasicDialog
          isOpen
          onClose={() => {
            if (!actionBusy) setCancelTarget(null);
          }}
          title={t("dlgSalaryHistoryCancelTitle", { ns: NS })}
          width="small"
        >
          <div className="space-y-4 p-1">
            <p className="text-sm text-gray-700">{t("msgSalaryHistoryCancelConfirm", { ns: NS })}</p>
            <div className="flex justify-end gap-2">
              <U2000ActionBtn
                label={t("btnCancel", { ns: NS })}
                handleClick={() => setCancelTarget(null)}
                variant="secondary"
                disabled={actionBusy}
              />
              <U2000ActionBtn
                label={t("btnConfirmCancelSalaryHistory", { ns: NS })}
                handleClick={() => void handleConfirmCancel()}
                disabled={actionBusy}
              />
            </div>
          </div>
        </U2000BasicDialog>
      ) : null}
    </div>
  );
};

export default U3101SalaryHistoryTab;
